import React from 'react';
import { useRedemptionRequest } from '../hooks/useRedemptionRequest';
import { notifyUser, getUserDetail, validateGhanaCard, validateNumberInput } from '../util/util';
import { useModalWidth } from '../hooks/useModalWidth';

function IdUploadCard({ label, file, onChange, error }) {
  const inputRef = React.useRef(null);
  const [isDragging, setIsDragging] = React.useState(false);
  const [preview, setPreview] = React.useState(null);

  React.useEffect(() => {
    if (file && file.type && file.type.startsWith('image/')) {
      const url = URL.createObjectURL(file);
      setPreview(url);
      return () => URL.revokeObjectURL(url);
    }
    setPreview(null);
  }, [file]);

  const handleFiles = (files) => {
    const f = files?.[0];
    if (!f) return;
    if (!f.type || !f.type.startsWith('image/')) {
      onChange(null);
      return;
    }
    onChange(f);
  };

  return (
    <div>
      <label style={{ display: 'block', color: '#555', fontWeight: 600, marginBottom: 8 }}>
        {label}
      </label>
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setIsDragging(false);
          handleFiles(e.dataTransfer.files);
        }}
        style={{
          border: `1.5px dashed ${error ? '#ff4d4f' : isDragging ? '#0D7C4B' : '#D1D5DB'}`,
          borderRadius: 10,
          background: isDragging ? '#f0fdf6' : '#F9FAFB',
          padding: '20px 16px',
          textAlign: 'center',
          cursor: 'pointer',
          transition: 'border-color 0.2s, background 0.2s',
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          onChange={(e) => {
            handleFiles(e.target.files);
            e.target.value = '';
          }}
          style={{ display: 'none' }}
        />
        {preview ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
            <img
              src={preview}
              alt={label}
              style={{
                width: 72,
                height: 72,
                objectFit: 'cover',
                borderRadius: 8,
                border: '1px solid #E5E7EB',
              }}
            />
            <div style={{ fontSize: 12, color: '#059669', fontWeight: 600, wordBreak: 'break-all', maxWidth: '100%' }}>
              {file.name}
            </div>
            <div style={{ fontSize: 11, color: '#9CA3AF' }}>Click to replace</div>
          </div>
        ) : (
          <>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 8,
                border: '1px solid #E5E7EB',
                background: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 14px',
              }}
            >
              <omk-icon style={{ fontSize: 18, color: '#6B7280' }}>upload</omk-icon>
            </div>
            <div style={{ fontSize: 13, marginBottom: 4 }}>
              <span style={{ fontWeight: 600, color: '#059669' }}>Click to upload</span>{' '}
              <span style={{ color: '#374151' }}>or drag and drop</span>
            </div>
            <div style={{ fontSize: 11, color: '#9CA3AF' }}>Image · JPG, PNG</div>
          </>
        )}
      </div>
      {error && (
        <div className="error-text" style={{ color: '#ff4d4f', marginTop: 4 }}>
          {error}
        </div>
      )}
    </div>
  );
}

const REDEMPTION_REASONS = [
  'None',
  'Death',
  'Voluntary Retirement',
  'Total Incapacity',
  'Permanent Emigration from Ghana',
  'Other (Specify)',
];

const WithdrawalRequestModal = ({ isOpen, onClose, policyNumber, productType, availableCashValue }) => {
  const modalWidth = useModalWidth();
  const user = React.useMemo(() => getUserDetail() || {}, []);
  const accountValue = React.useMemo(
    () => (Number(availableCashValue) > 0 ? Number(availableCashValue) : null),
    [availableCashValue],
  );
  const [nationalId, setNationalId] = React.useState(user?.ghana_card_id || user?.ghana_card_number || '');
  const [redemptionType, setRedemptionType] = React.useState('Partial');
  const [redemptionMode, setRedemptionMode] = React.useState('amount');
  const [amount, setAmount] = React.useState('');
  const [percentage, setPercentage] = React.useState('');
  const [redemptionReason, setRedemptionReason] = React.useState('');
  const [customReason, setCustomReason] = React.useState('');
  const [reasonError, setReasonError] = React.useState('');
  const [bankName, setBankName] = React.useState('');
  const [bankAccount, setBankAccount] = React.useState('');
  const [accountHolderName, setAccountHolderName] = React.useState(user?.name || '');
  const [bankBranch, setBankBranch] = React.useState('');
  const [idFront, setIdFront] = React.useState(null);
  const [idBack, setIdBack] = React.useState(null);
  const [nationalIdError, setNationalIdError] = React.useState('');
  const [amountError, setAmountError] = React.useState('');
  const [percentageError, setPercentageError] = React.useState('');
  const [idFrontError, setIdFrontError] = React.useState('');
  const [idBackError, setIdBackError] = React.useState('');

  const redemptionRequestMutation = useRedemptionRequest();

  const omkTextFieldStyles = React.useMemo(() => ({
    width: '100%',
    '--omk-textfield-height': '48px',
    '--omk-textfield-border-radius': '10px',
    '--omk-textfield-padding': '0 16px',
    '--omk-textfield-background': '#fff',
    '--omk-textfield-border-color': '#ddd',
    '--omk-textfield-hover-border-color': '#0D7C4B',
  }), []);

  const resetForm = React.useCallback(() => {
    setNationalId(user?.ghana_card_id || user?.ghana_card_number || '');
    setRedemptionType('Partial');
    setRedemptionMode('amount');
    setAmount('');
    setPercentage('');
    setRedemptionReason('');
    setCustomReason('');
    setReasonError('');
    setBankName('');
    setBankAccount('');
    setAccountHolderName(user?.name || '');
    setBankBranch('');
    setIdFront(null);
    setIdBack(null);
    setNationalIdError('');
    setAmountError('');
    setPercentageError('');
    setIdFrontError('');
    setIdBackError('');
  }, [user]);

  const handleClose = React.useCallback(() => {
    resetForm();
    onClose();
  }, [onClose, resetForm]);

  const handleNationalIdChange = (value) => {
    const normalized = value.toUpperCase();
    setNationalId(normalized);
    setNationalIdError(normalized && !validateGhanaCard(normalized) ? 'Enter a valid Ghana Card number.' : '');
  };

  const handleAmountChange = (value) => {
    if (!validateNumberInput(value)) {
      return;
    }

    setAmount(value);
    if (redemptionType === 'Partial' && !value && !percentage) {
      setAmountError('Enter an amount or percentage for partial redemption.');
    } else if (value && Number(value) <= 0) {
      setAmountError('Amount must be greater than 0.');
    } else if (value && accountValue && Number(value) > accountValue) {
      setAmountError(`Amount cannot exceed your current account value (GHS ${accountValue.toLocaleString('en-US', { maximumFractionDigits: 2 })}).`);
    } else {
      setAmountError('');
      if (value) setPercentageError('');
    }
  };

  const handlePercentageChange = (value) => {
    if (!validateNumberInput(value)) {
      return;
    }

    setPercentage(value);
    const numericValue = Number(value);
    if (redemptionType === 'Partial' && !value && !amount) {
      setPercentageError('Enter an amount or percentage for partial redemption.');
    } else if (value && (numericValue <= 0 || numericValue > 100)) {
      setPercentageError('Percentage must be between 0.01 and 100.');
    } else {
      setPercentageError('');
      if (value) setAmountError('');
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!nationalId || !validateGhanaCard(nationalId)) {
      setNationalIdError('Enter a valid Ghana Card number.');
      notifyUser('error', 'Please provide a valid Ghana Card number.');
      return;
    }

    const finalReason =
      redemptionReason === 'Other (Specify)' ? customReason : redemptionReason;
    if (!finalReason.trim()) {
      setReasonError(
        redemptionReason === 'Other (Specify)'
          ? 'Please specify the redemption reason.'
          : 'Please select a redemption reason.',
      );
      notifyUser('error', 'Please provide a redemption reason.');
      return;
    }

    if (!bankName.trim() || !bankAccount.trim() || !accountHolderName.trim() || !bankBranch.trim()) {
      notifyUser('error', 'Please complete all bank account fields.');
      return;
    }

    if (redemptionType === 'Partial' && redemptionMode === 'amount') {
      if (!amount) {
        setAmountError('Enter an amount for partial redemption.');
        notifyUser('error', 'Partial redemption requires an amount.');
        return;
      }
      if (accountValue && Number(amount) > accountValue) {
        setAmountError(`Amount cannot exceed your current account value (GHS ${accountValue.toLocaleString('en-US', { maximumFractionDigits: 2 })}).`);
        notifyUser('error', 'Amount exceeds your current account value.');
        return;
      }
    }

    if (redemptionType === 'Partial' && redemptionMode === 'percentage' && !percentage) {
      setPercentageError('Enter a percentage for partial redemption.');
      notifyUser('error', 'Partial redemption requires a percentage.');
      return;
    }

    if (!idFront || !idBack) {
      if (!idFront) setIdFrontError('Please upload the front of your Ghana Card.');
      if (!idBack) setIdBackError('Please upload the back of your Ghana Card.');
      notifyUser('error', 'Please upload both sides of your Ghana Card.');
      return;
    }

    redemptionRequestMutation.mutate({
      nationalId,
      redemptionType,
      amount,
      percentage,
      redemptionReason: finalReason,
      bankName,
      bankAccount,
      accountHolderName,
      bankBranch,
      idFront,
      idBack,
    }, {
      onSuccess: () => {
        handleClose();
        notifyUser('success', 'Withdrawal request submitted successfully.');
      },
      onError: (error) => {
        notifyUser('error', error?.response?.data?.message || error?.message || 'Failed to submit withdrawal request. Please try again.');
      },
    });
  };

  if (!isOpen) return null;

  return (
    <div
      className="withdrawal-modal-overlay"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        background: 'rgba(0,0,0,0.18)',
        zIndex: 10000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
      onClick={handleClose}
    >
      <style>{`
        @media (max-width: 768px) {
          .withdrawal-modal-overlay {
            align-items: flex-start !important;
            padding-top: 40px !important;
          }
        }
      `}</style>
      <div
        style={{
          background: '#fff',
          borderRadius: 16,
          width: '100%',
          maxWidth: modalWidth,
          maxHeight: '85vh',
          overflowY: 'auto',
          padding: 0,
          boxShadow: '0 16px 60px rgba(0,0,0,0.15)'
        }}
        onClick={(event) => event.stopPropagation()}
      >
        {/* Header */}
        <div style={{ padding: '24px 32px 20px 32px' }}>
          <div className="modal-header-title" style={{ fontWeight: 800, color: '#222', marginBottom: 6, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>Withdrawal Request</span>
            <button onClick={handleClose} style={{ background: 'none', border: 'none', fontSize: 22, color: '#888', cursor: 'pointer', marginLeft: 12, fontWeight: 700, lineHeight: 1 }} aria-label="Close">×</button>
          </div>
          <p className="modal-header-desc" style={{ color: '#888', marginBottom: 0, marginTop: 0 }}>
            Fill in the redemption form details for this withdrawal request.
          </p>
        </div>

        {/* Content */}
        <div style={{ padding: '0 32px 32px 32px' }}>
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <div>
            <omk-text-field
              label="Ghana Card Number"
              name="national_id"
              type="text"
              value={nationalId}
              onInput={(e) => handleNationalIdChange(e.target.value)}
              required
              style={{
                ...omkTextFieldStyles,
                ...(nationalIdError ? { '--omk-textfield-border-color': '#ff4d4f' } : {})
              }}
              placeholder="e.g., GHA-123456789-1 or NRG-000000000-A"
            />
            {nationalIdError && (
              <div className="error-text" style={{ color: '#ff4d4f', marginTop: 4 }}>
                {nationalIdError}
              </div>
            )}
          </div>
          <div>
            <omk-select
              label="Redemption Type"
              name="redemption_type"
              value={redemptionType}
              onInput={(e) => setRedemptionType(e.target.value)}
            >
              <omk-select-option value="Partial">Partial</omk-select-option>
              <omk-select-option value="Total Redemption">Total Redemption</omk-select-option>
            </omk-select>
          </div>
          {redemptionType === 'Partial' && (
            <>
              <div>
                <omk-select
                  label="Redemption Option"
                  name="redemption_option"
                  value={redemptionMode}
                  onInput={(e) => {
                    setRedemptionMode(e.target.value);
                    setAmountError('');
                    setPercentageError('');
                  }}
                >
                  <omk-select-option value="amount">Amount</omk-select-option>
                  <omk-select-option value="percentage">Percentage</omk-select-option>
                </omk-select>
              </div>
              {redemptionMode === 'amount' ? (
                <div>
                  <omk-text-field
                    label="Partial Redemption Amount"
                    name="amount"
                    type="text"
                    value={amount}
                    onInput={(e) => handleAmountChange(e.target.value)}
                    style={{
                      ...omkTextFieldStyles,
                      ...(amountError ? { '--omk-textfield-border-color': '#ff4d4f' } : {})
                    }}
                    placeholder="Enter amount"
                  />
                  {amountError && (
                    <div className="error-text" style={{ color: '#ff4d4f', marginTop: 4 }}>
                      {amountError}
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <omk-text-field
                    label="Partial Redemption Percentage"
                    name="percentage"
                    type="text"
                    value={percentage}
                    onInput={(e) => handlePercentageChange(e.target.value)}
                    style={{
                      ...omkTextFieldStyles,
                      ...(percentageError ? { '--omk-textfield-border-color': '#ff4d4f' } : {})
                    }}
                    placeholder="Enter percentage"
                  />
                  {percentageError && (
                    <div className="error-text" style={{ color: '#ff4d4f', marginTop: 4 }}>
                      {percentageError}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
          <div>
            <omk-select
              label="Redemption Reason"
              name="redemption_reason"
              value={redemptionReason}
              onInput={(e) => {
                setRedemptionReason(e.target.value);
                setReasonError('');
                setCustomReason('');
              }}
            >
              <omk-select-option value="">Select a reason</omk-select-option>
              {REDEMPTION_REASONS.map((reason) => (
                <omk-select-option key={reason} value={reason}>
                  {reason}
                </omk-select-option>
              ))}
            </omk-select>
            {reasonError && (
              <div className="error-text" style={{ color: '#ff4d4f', marginTop: 4 }}>
                {reasonError}
              </div>
            )}
          </div>
          {redemptionReason === 'Other (Specify)' && (
            <div>
              <omk-text-field
                label="Specify Redemption Reason"
                name="redemption_reason_custom"
                type="text"
                value={customReason}
                onInput={(e) => setCustomReason(e.target.value)}
                required
                style={{
                  ...omkTextFieldStyles,
                  ...(reasonError ? { '--omk-textfield-border-color': '#ff4d4f' } : {})
                }}
                placeholder="Enter the reason for this withdrawal request"
              />
            </div>
          )}
          <div>
            <omk-text-field
              label="Bank Name"
              name="bank_name"
              type="text"
              value={bankName}
              onInput={(e) => setBankName(e.target.value)}
              required
              style={omkTextFieldStyles}
              placeholder="Enter bank name"
            />
          </div>
          <div>
            <omk-text-field
              label="Bank Account Number"
              name="bank_account"
              type="text"
              value={bankAccount}
              onInput={(e) => setBankAccount(e.target.value)}
              required
              style={omkTextFieldStyles}
              placeholder="Enter bank account number"
            />
          </div>
          <div>
            <omk-text-field
              label="Account Holder Name"
              name="account_holder_name"
              type="text"
              value={accountHolderName}
              onInput={(e) => setAccountHolderName(e.target.value)}
              required
              style={omkTextFieldStyles}
              placeholder="Enter account holder name"
            />
          </div>
          <div>
            <omk-text-field
              label="Bank Branch"
              name="bank_branch"
              type="text"
              value={bankBranch}
              onInput={(e) => setBankBranch(e.target.value)}
              required
              style={omkTextFieldStyles}
              placeholder="Enter bank branch"
            />
          </div>
          <div style={{ display: 'grid', gap: 16, gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
            <IdUploadCard
              label="Ghana Card Front"
              file={idFront}
              onChange={(f) => {
                setIdFront(f);
                setIdFrontError('');
              }}
              error={idFrontError}
            />
            <IdUploadCard
              label="Ghana Card Back"
              file={idBack}
              onChange={(f) => {
                setIdBack(f);
                setIdBackError('');
              }}
              error={idBackError}
            />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 4 }}>
            <button type="button" className="btn btn-white" onClick={handleClose} style={{ padding: '10px 18px', fontWeight: 600 }}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={redemptionRequestMutation.isLoading}>
              {redemptionRequestMutation.isLoading ? 'Submitting...' : 'Submit'}
            </button>
          </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default WithdrawalRequestModal;
